/*
  User-defined colour theme.

  The fixed palettes live in styles.css. A custom one can't, because its colour
  is only known at runtime — so it is derived here from the teal base tokens the
  same way those palettes were: rotate every hue onto the picked colour, scale
  chroma to match it, and set the result as inline variables on <html>.
*/

export type CustomTheme = {
  /** Shown in the theme menu and in place of "Somasi Ops" in the sidebar. */
  name: string;
  /** The brand colour as #rrggbb. */
  color: string;
  /** Sidebar mark as a small PNG data URL, or null to keep the Somasi mark. */
  logo: string | null;
};

export const DEFAULT_CUSTOM_THEME: CustomTheme = { name: "My Theme", color: "#7c3aed", logo: null };

type Lch = [number, number, number];

/* Mirrors :root and .dark in styles.css (status colours excluded — they keep meaning). */
const BASE: Record<"light" | "dark", Record<string, Lch>> = {
  light: {
    "--background": [0.99, 0.005, 180],
    "--foreground": [0.2, 0.02, 200],
    "--card": [1, 0, 0],
    "--card-foreground": [0.2, 0.02, 200],
    "--popover": [1, 0, 0],
    "--popover-foreground": [0.2, 0.02, 200],
    "--primary": [0.44, 0.07, 190],
    "--secondary": [0.95, 0.015, 190],
    "--secondary-foreground": [0.3, 0.05, 195],
    "--muted": [0.96, 0.008, 190],
    "--muted-foreground": [0.48, 0.02, 210],
    "--accent": [0.92, 0.03, 190],
    "--accent-foreground": [0.3, 0.05, 195],
    "--border": [0.9, 0.01, 200],
    "--input": [0.9, 0.01, 200],
    "--ring": [0.44, 0.07, 190],
    "--sidebar": [0.24, 0.04, 195],
    "--sidebar-foreground": [0.95, 0.01, 180],
    "--sidebar-primary": [0.54, 0.1, 180],
    "--sidebar-accent": [0.32, 0.05, 195],
    "--sidebar-accent-foreground": [0.98, 0, 0],
    "--sidebar-border": [0.32, 0.04, 195],
    "--sidebar-ring": [0.6, 0.1, 180],
  },
  dark: {
    "--background": [0.16, 0.02, 200],
    "--foreground": [0.96, 0.01, 180],
    "--card": [0.22, 0.03, 200],
    "--card-foreground": [0.96, 0.01, 180],
    "--popover": [0.22, 0.03, 200],
    "--popover-foreground": [0.96, 0.01, 180],
    "--primary": [0.54, 0.1, 185],
    "--secondary": [0.28, 0.03, 200],
    "--secondary-foreground": [0.96, 0.01, 180],
    "--muted": [0.28, 0.03, 200],
    "--muted-foreground": [0.7, 0.02, 200],
    "--accent": [0.32, 0.04, 195],
    "--accent-foreground": [0.96, 0.01, 180],
    "--border": [0.3, 0.03, 200],
    "--input": [0.3, 0.03, 200],
    "--ring": [0.65, 0.1, 185],
    "--sidebar": [0.2, 0.03, 200],
    "--sidebar-foreground": [0.95, 0.01, 180],
    "--sidebar-primary": [0.54, 0.1, 180],
    "--sidebar-accent": [0.28, 0.04, 195],
    "--sidebar-accent-foreground": [0.96, 0.01, 180],
    "--sidebar-border": [0.28, 0.03, 200],
    "--sidebar-ring": [0.65, 0.1, 180],
  },
};

/** Every variable a custom theme sets, so switching away can clear them all. */
export const CUSTOM_VARS = [
  ...Object.keys(BASE.light),
  "--primary-foreground",
  "--sidebar-primary-foreground",
];

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
const fmt = ([l, c, h]: Lch) => `oklch(${l.toFixed(3)} ${c.toFixed(3)} ${h.toFixed(1)})`;

export function hexToOklch(hex: string): Lch {
  const n = parseInt(hex.replace("#", ""), 16);
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => {
    const s = v / 255;
    return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  const L = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
  const A = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const B = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;
  const C = Math.hypot(A, B);
  const H = ((Math.atan2(B, A) * 180) / Math.PI + 360) % 360;
  return [L, C, H];
}

/** WCAG relative luminance of an OKLCH colour (clipped to sRGB). */
function luminance([L, C, H]: Lch) {
  const a = C * Math.cos((H * Math.PI) / 180);
  const b = C * Math.sin((H * Math.PI) / 180);
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3;
  const [r, g, bl] = [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ].map((x) => clamp(x, 0, 1));
  return 0.2126 * r + 0.7152 * g + 0.0722 * bl;
}

const WHITE: Lch = [0.99, 0, 0];
const INK: Lch = [0.18, 0, 0];

const contrast = (a: Lch, b: Lch) => {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
};

/** White or near-black, whichever reads better on `bg` — so pale picks like yellow still work. */
function onColor(bg: Lch): Lch {
  return contrast(WHITE, bg) >= contrast(INK, bg) ? WHITE : INK;
}

/*
  Walks a brand colour's lightness away from the page until it is usable: at
  least 3:1 against every surface it sits on (links, focus rings, outlines) and
  4.5:1 for the text drawn on it — with a little headroom, since the values are
  rounded when written out. A pale pick like yellow on a white page comes out as a
  deeper gold rather than a button nobody can see.
*/
function readable(start: Lch, surfaces: Lch[]): Lch {
  const step = luminance(surfaces[0]) > 0.5 ? -0.01 : 0.01;
  let c: Lch = start;
  for (let i = 0; i < 100; i++) {
    if (surfaces.every((sf) => contrast(c, sf) >= 3.1) && contrast(onColor(c), c) >= 4.6) break;
    c = [clamp(c[0] + step, 0.05, 0.98), c[1], c[2]];
  }
  return c;
}

export function customThemeVars(color: string, mode: "light" | "dark"): Record<string, string> {
  const [uL, uC, uH] = hexToOklch(color);
  // Teal's brand chroma is ~0.07; scale the neutrals by how much more (or less)
  // colourful the pick is, damped so tinted greys stay greys.
  const ratio = uC / 0.07;
  const neutral = clamp(ratio * 0.7, 0.15, 1.4);
  const out: Record<string, string> = {};

  for (const [k, [l, c, h]] of Object.entries(BASE[mode])) {
    const hue = c === 0 ? 0 : (uH + (h - 190) + 360) % 360;
    out[k] = fmt([l, c === 0 ? 0 : Math.min(0.2, c * neutral), hue]);
  }

  // Brand tokens use the picked colour itself, nudged lighter in dark mode and
  // then pushed until they read against their own surface.
  const chroma = Math.min(uC, 0.25);
  const surfaces = [BASE[mode]["--background"], BASE[mode]["--card"]];
  const primary = readable(
    mode === "light" ? [uL, chroma, uH] : [clamp(uL + 0.1, 0.5, 0.85), chroma, uH],
    surfaces,
  );
  const sidebarPrimary = readable(
    [clamp(uL + 0.1, 0.5, 0.85), chroma, uH],
    [BASE[mode]["--sidebar"], BASE[mode]["--sidebar-accent"]],
  );
  out["--primary"] = fmt(primary);
  out["--ring"] = fmt(primary);
  out["--primary-foreground"] = fmt(onColor(primary));
  out["--sidebar-primary"] = fmt(sidebarPrimary);
  out["--sidebar-ring"] = fmt(sidebarPrimary);
  out["--sidebar-primary-foreground"] = fmt(onColor(sidebarPrimary));
  return out;
}

/** Shrinks an uploaded image to a 128px PNG so it fits comfortably in localStorage. */
export function imageFileToDataUrl(file: File, size = 128): Promise<string> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, size / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(img.width * scale));
      canvas.height = Math.max(1, Math.round(img.height * scale));
      canvas.getContext("2d")?.drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("That file could not be read as an image."));
    };
    img.src = url;
  });
}
